/**
 * The caller's own account: `/me` and what hangs off it.
 */
import type {
  ClaimableStudioResponse,
  MeResponse,
  MySeatInvitationsResponse,
  SetHandleRequest,
  DeclareTypesRequest,
  UpdateProfileRequest,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

export const fetchMe = () => request<MeResponse>("/me");

/**
 * The studio sitting dormant at the caller's own sign-in address, if a
 * collector named one by email on an authorship claim — OND-6, read by the
 * first screen after sign-in (OND-8 decision 3). `studio` is null for almost
 * everybody, and null means draw nothing.
 */
export const fetchClaimableStudio = () =>
  request<ClaimableStudioResponse>("/me/claimable-studio");

/**
 * Takes the studio's seat — OND-8 decision 1's deliberate step, OND-7's screen.
 *
 * A POST with no body of its own: the route reads the caller's address off
 * their session and nothing else. The whole `MeResponse` comes back because the
 * claim moves `capacities` — the derived verifier becomes the studio's estate —
 * and therefore which workspaces exist, which only the server may say.
 */
export const claimStudio = () => request<MeResponse>("/me/claim-studio", {});

/**
 * Corrects the caller's own name, phone and country.
 *
 * Sends all three every time and answers with the whole `MeResponse`, because
 * `ondo.update_person()` replaces rather than patches — a null phone here means
 * "clear it", never "leave it alone". See UpdateProfileRequest.
 */
export const updateProfile = (body: UpdateProfileRequest) =>
  requestWith<MeResponse>("PUT", "/me/profile", body);

/**
 * Adds to what the account says it is. Never removes.
 *
 * PUT, like `updateProfile`, and idempotent in a stronger sense than that one:
 * sending a type the account already holds changes nothing. The whole
 * `MeResponse` comes back because this moves `capacities`, and therefore
 * `workspaces`, and therefore which screens exist — a derivation the server is
 * the only authority on.
 */
export const declareTypes = (body: DeclareTypesRequest) =>
  requestWith<MeResponse>("PUT", "/me/declared-types", body);

/**
 * Claims the account's public address, once.
 *
 * Filed under `/me` because a handle is a property of the principal; viewing
 * rooms are merely the first thing to use it.
 */
export const setMyHandle = (body: SetHandleRequest) =>
  requestWith<{ handle: string }>("PUT", "/me/handle", body);

/**
 * The seats a firm has invited this account to take — OND-24.
 *
 * Under `/me` rather than under the firm, which is the address saying who may
 * ask: an invitee holds nothing at that firm, so there is no firm-scoped route
 * available to them. No id is sent either — the server reads the caller's own
 * confirmed address off their session, which is `fetchClaimableStudio`'s
 * mechanism and removes the whole class of walkable-id problem.
 *
 * An empty list is the answer for almost everybody, and an empty list means
 * draw nothing.
 */
export const fetchSeatInvitations = () =>
  request<MySeatInvitationsResponse>("/me/seat-invitations");

/**
 * Takes the seat — OND-24, and the act that makes the membership exist.
 *
 * The seat is written by the statement this request runs, in its own
 * transaction, with every recheck in the WHERE clause that writes it. OND-24
 * asked for a worker to attach it after waiting for the signup;
 * `20261014092200` decision 6 answers that — a seat that appeared when a queue
 * drained would not be a seat.
 *
 * The whole `MeResponse` comes back because this moves `capacities`, and
 * therefore `workspaces`, and therefore which screens exist. A 403 in one
 * sentence for every refusal, a second press included.
 */
export const acceptSeatInvitation = (invitationId: string) =>
  request<MeResponse>(`/me/seat-invitations/${encodeURIComponent(invitationId)}/accept`, {});

/**
 * Says no — OND-24. A POST to a verb rather than a DELETE on the invitation,
 * because the invitee does not own it: they are answering somebody else's offer,
 * and a DELETE would say they had removed it.
 *
 * Declining suppresses nothing. The firm may ask again, and whether to is its
 * judgement rather than a rule — `20261012096500` decision 2.
 */
export const declineSeatInvitation = (invitationId: string) =>
  request<MeResponse>(`/me/seat-invitations/${encodeURIComponent(invitationId)}/decline`, {});
